// src/kunde/kerne.js
// Appens skal: login, sidevisning, menu og tema.
//
// Bemærk at opstarten ligger i start(), ikke som bivirkninger i toppen af
// filen. Da alt lå i én <script>-blok, var rækkefølgen givet af hvor
// koden stod. Med moduler afhænger den af hvornår hvert modul tilfældigvis
// bliver evalueret — og det er ikke noget man skal skulle gætte sig til.
// main.js kalder start() når alt andet er på plads.

import { loadMachines } from './maskiner.js';
import { showPwaBannerIfLoggedIn } from './pwa.js';
import { stopScan } from './scanner.js';
import { sess, setSess } from './state.js';
import { loadVideoPage } from './videoer.js';
import { sb } from '../lib/supabase.js';

// ── OPSTART ───────────────────────────────────
/**
 * Sat mens brugeren er på vej gennem et nulstillingslink. Forhindrer, at
 * appen logger dem direkte ind, før den nye adgangskode er gemt.
 */
let venterPaaNyKode = false;

export function start() {
  anvendGemtTema();
  showPage('loading');

  document.addEventListener('keydown', e => {
    if (e.key === 'Escape') closeDrawer();
  });

  // Lytteren registreres altid, også i nulstillingsforløbet — ellers
  // kører appen uden den, når brugeren er kommet igennem, og et senere
  // log ud ville ikke opdatere skærmen. Den er ufarlig her, fordi
  // venterPaaNyKode holder den tilbage, indtil koden er gemt.
  sb.auth.onAuthStateChange(haandterAuthAendring);

  gendanVisning();

  // Skal ske FØR sessionen gendannes. Et nulstillingslink bærer et
  // gyldigt token i adressen, så uden dette tjek ville brugeren blive
  // logget lige ind — og aldrig få lov at vælge en ny adgangskode.
  //
  // Rækkefølgen holder, fordi flaget sættes synkront her, mens
  // auth-hændelsen fra tokenet først kommer efter et netværkskald.
  if (tjekNulstillingsLink()) return;

  gendanSession();
}

/**
 * Undersøger, om siden er åbnet fra et nulstillingslink.
 * @returns {boolean} true hvis vi overtog visningen.
 */
function tjekNulstillingsLink() {
  const hash = window.location.hash || '';
  const soeg = new URLSearchParams(window.location.search);

  // Udløbet eller allerede brugt link
  if (hash.includes('error=') || soeg.get('error')) {
    history.replaceState(null, '', window.location.pathname);
    showPage('auth');
    switchAuth('login');
    showErr('Linket er udløbet eller allerede brugt. Bed om et nyt nedenfor.');
    return true;
  }

  if (!hash.includes('type=recovery')) return false;

  venterPaaNyKode = true;
  showPage('auth');
  visSaetAdgangskode();
  return true;
}

function visSaetAdgangskode() {
  ['loginForm', 'regForm', 'forgotForm'].forEach(id => {
    const el = document.getElementById(id);
    if (el) el.style.display = 'none';
  });
  document.getElementById('setPassForm').style.display = 'block';
  document.querySelector('.atabs')?.style.setProperty('display', 'none');
  clearMsg();
}

/** Gemmer den nye adgangskode og logger brugeren ind. */
export async function doSetPassword() {
  const pass  = document.getElementById('sp1').value;
  const pass2 = document.getElementById('sp2').value;
  const btn   = document.getElementById('spBtn');
  clearMsg();

  if (pass.length < 8)  { showErr('Adgangskoden skal være mindst 8 tegn'); return; }
  if (pass !== pass2)   { showErr('De to adgangskoder er ikke ens'); return; }

  btn.disabled = true; btn.textContent = 'Gemmer…';
  try {
    const { data, error } = await sb.auth.updateUser({ password: pass });
    if (error) { showErr('Fejl: ' + error.message); return; }

    // Tokenet skal ikke blive stående i adressen efter brug.
    history.replaceState(null, '', window.location.pathname);
    venterPaaNyKode = false;
    showOk('✓ Adgangskoden er gemt — logger dig ind…');

    setTimeout(async () => {
      document.getElementById('setPassForm').style.display = 'none';
      document.querySelector('.atabs')?.style.removeProperty('display');
      switchAuth('login');
      if (data?.user) {
        await loadProfile(data.user);
        visIndlogget();
      } else {
        gendanSession();
      }
    }, 900);
  } catch (e) {
    showErr('Netværksfejl: ' + e.message);
  } finally {
    btn.disabled = false; btn.textContent = 'Gem adgangskode';
  }
}

// Session tjekkes aktivt ved opstart — det virker bedre end at vente på
// onAuthStateChange, når appen åbnes som PWA eller uden netværk.
async function gendanSession() {
  try {
    const { data } = await Promise.race([
      sb.auth.getSession(),
      new Promise((_, r) => setTimeout(() => r(new Error('timeout')), 5000)),
    ]);
    if (data?.session) {
      setSess(data.session);
      try {
        await Promise.race([
          loadProfile(data.session.user),
          new Promise((_, r) => setTimeout(() => r(), 3000)),
        ]);
      } catch (e) { /* falder tilbage til e-mail som navn */ }
      visIndlogget();
      return;
    }
  } catch (e) { /* timeout eller netværksfejl — vis login */ }
  showPage('auth');
}

async function haandterAuthAendring(event, session) {
  // Supabase udsender SIGNED_IN, når tokenet fra nulstillingslinket
  // læses. Uden dette ville brugeren ryge direkte ind i appen i stedet
  // for at få lov at vælge en ny adgangskode.
  if (venterPaaNyKode) return;

  if (event === 'PASSWORD_RECOVERY') {
    venterPaaNyKode = true;
    showPage('auth');
    visSaetAdgangskode();
    return;
  }

  setSess(session);

  if (!session) {
    showPage('auth');
    document.getElementById('bnav').classList.remove('on');
    document.body.classList.remove('has-bnav');
    document.getElementById('burgerBtn').style.display = 'none';
    updateHeader();
    updateDrawer();
    return;
  }

  try {
    await Promise.race([
      loadProfile(session.user),
      new Promise((_, r) => setTimeout(() => r(new Error('timeout')), 5000)),
    ]);
  } catch (e) {
    // Profilopslaget fejlede — vis e-mailen som navn frem for ingenting
    session.user._name = session.user.email.split('@')[0];
    const init = session.user._name[0].toUpperCase();
    document.getElementById('dav').textContent = init;
    document.getElementById('dname').textContent = 'Hej, ' + session.user._name + '!';
    const hav = document.getElementById('hav');
    const hname = document.getElementById('hname');
    if (hav) hav.textContent = init;
    if (hname) hname.textContent = session.user._name;
  }

  visIndlogget();
  document.getElementById('burgerBtn').style.display = '';
  updateHeader();
  updateDrawer();
}

function visIndlogget() {
  showPage('dash');
  loadMachines();
  updateHeader();
  showPwaBannerIfLoggedIn();
  document.getElementById('bnav').classList.add('on');
  document.body.classList.add('has-bnav');
}

// ── VISNING AF MASKINLISTEN ───────────────────
const VISNING_NOEGLE = 'dktec-maskinvisning';

/**
 * Liste eller gitter. Valget huskes på tværs af besøg.
 *
 * Standard er liste: på en bred skærm giver rækker mest overblik, og
 * under 820 px falder de af sig selv tilbage til kort — dér er der kun
 * én fornuftig opstilling, og derfor er knapperne også skjult.
 */
export function vaelgVisning(hvad) {
  const liste = document.getElementById('mcList');
  if (!liste) return;
  const gitter = hvad === 'gitter';
  liste.classList.toggle('som-gitter', gitter);
  document.getElementById('visGitter')?.setAttribute('aria-pressed', String(gitter));
  document.getElementById('visListe')?.setAttribute('aria-pressed', String(!gitter));
  try { localStorage.setItem(VISNING_NOEGLE, hvad); } catch (e) { /* privat tilstand */ }
}

/** Genskaber det valg brugeren traf sidst. Kaldes fra start(). */
function gendanVisning() {
  let valg = 'liste';
  try { valg = localStorage.getItem(VISNING_NOEGLE) || 'liste'; } catch (e) { /* privat tilstand */ }
  vaelgVisning(valg);
}

// ── MENU ──────────────────────────────────────
export function openDrawer() {
  document.getElementById('drawer').classList.add('open');
  document.getElementById('drawerBg').classList.add('open');
  document.body.style.overflow = 'hidden';
}

export function closeDrawer() {
  document.getElementById('drawer').classList.remove('open');
  document.getElementById('drawerBg').classList.remove('open');
  document.body.style.overflow = '';
}

export function updateDrawer() {
  const loggedIn = !!sess;
  document.getElementById('drawerUser').classList.toggle('on', loggedIn);
  document.getElementById('drawerFoot').classList.toggle('on', loggedIn);
  if (loggedIn) {
    const name = sess.user._name || sess.user.email.split('@')[0];
    document.getElementById('drawerUname').textContent = name;
    document.getElementById('drawerUemail').textContent = sess.user.email;
    document.getElementById('drawerAuthBtn').textContent = 'Log ud';
  }
}

export function setDrawerActive(page) {
  document.querySelectorAll('.drawer-item').forEach(el => el.classList.remove('active'));
  document.getElementById('drawer-' + page)?.classList.add('active');
}

// ── PROFIL OG LOGIN ───────────────────────────
export async function loadProfile(user) {
  const { data } = await sb.from('profiles').select('name').eq('id', user.id).single();
  user._name = data?.name || user.email.split('@')[0];
  const init = user._name[0].toUpperCase();
  const fornavn = user._name.split(' ')[0];
  document.getElementById('dav').textContent = init;
  document.getElementById('dname').textContent = 'Hej, ' + fornavn + '!';
  const hav = document.getElementById('hav');
  const hname = document.getElementById('hname');
  if (hav) hav.textContent = init;
  if (hname) hname.textContent = fornavn;
  updateDrawer();
}

export function switchAuth(mode) {
  ['loginForm', 'regForm', 'forgotForm'].forEach(id => {
    document.getElementById(id).style.display = 'none';
  });
  const id = mode === 'login' ? 'loginForm' : mode === 'register' ? 'regForm' : 'forgotForm';
  document.getElementById(id).style.display = 'block';
  document.getElementById('loginTab').classList.toggle('active', mode === 'login');
  document.getElementById('regTab').classList.toggle('active', mode === 'register');
  clearMsg();
}

export function showErr(msg) {
  const e = document.getElementById('aerr');
  e.textContent = msg; e.classList.add('on');
  document.getElementById('aok').classList.remove('on');
}

export function showOk(msg) {
  const e = document.getElementById('aok');
  e.textContent = msg; e.classList.add('on');
  document.getElementById('aerr').classList.remove('on');
}

export function clearMsg() {
  document.getElementById('aerr').classList.remove('on');
  document.getElementById('aok').classList.remove('on');
}

export async function doLogin() {
  const email = document.getElementById('le').value.trim();
  const pass = document.getElementById('lp').value;
  if (!email || !pass) { showErr('Udfyld email og adgangskode'); return; }

  const btn = document.getElementById('loginBtn');
  btn.disabled = true; btn.textContent = 'Logger ind…';
  try {
    const { data, error } = await sb.auth.signInWithPassword({ email, password: pass });
    if (error) { showErr('Fejl: ' + error.message); }
    else if (data?.session) {
      setSess(data.session);
      await loadProfile(data.session.user);
      visIndlogget();
    } else { showErr('Ingen session — prøv igen'); }
  } catch (e) {
    showErr('Netværksfejl: ' + e.message);
  } finally {
    btn.disabled = false; btn.textContent = 'Log ind';
  }
}

export async function doRegister() {
  const name = document.getElementById('rn').value.trim();
  const email = document.getElementById('re').value.trim();
  const pass = document.getElementById('rp').value;
  if (!name || !email || !pass) { showErr('Udfyld alle felter'); return; }
  if (pass.length < 6) { showErr('Adgangskode skal være mindst 6 tegn'); return; }

  const btn = document.getElementById('regBtn');
  btn.disabled = true; btn.textContent = 'Opretter…';
  try {
    const { data, error } = await sb.auth.signUp({
      email, password: pass, options: { data: { name } },
    });
    if (error) { showErr(error.message); return; }
    if (data?.user) {
      await sb.from('profiles').upsert({ id: data.user.id, name },
        { onConflict: 'id', ignoreDuplicates: true });
    }
    showOk('✓ Konto oprettet! Du kan nu logge ind.');
    setTimeout(() => switchAuth('login'), 1500);
  } catch (e) {
    showErr('Fejl: ' + e.message);
  } finally {
    btn.disabled = false; btn.textContent = 'Opret konto';
  }
}

export async function doForgot() {
  const email = document.getElementById('fe').value.trim();
  if (!email) return;
  // Afsluttende skråstreg er ikke pynt: Supabases hvidliste bruger
  // mønstre som https://domæne/**, og dem rammer en bar adresse uden
  // skråstreg ikke. Uden den falder linket tilbage til sitets
  // standardadresse, og kunden lander et andet sted end der, hvor de
  // bad om linket.
  await sb.auth.resetPasswordForEmail(email, { redirectTo: window.location.origin + '/' });
  showOk('✓ Vi har sendt et link til ' + email + '. Åbn det for at vælge en ny adgangskode.');
  switchAuth('login');
}

export function handleAuthBtn() {
  if (sess) sb.auth.signOut();
  else showPage('auth');
}

export function updateHeader() {
  const upill = document.getElementById('upill');
  if (upill) upill.style.display = sess ? 'flex' : 'none';
  document.getElementById('authBtn').textContent = sess ? 'Log ud' : 'Log ind';
}

// ── SIDER OG FANER ────────────────────────────
export function showPage(n) {
  document.querySelectorAll('.page').forEach(p => p.classList.remove('active'));
  document.getElementById('page-' + n).classList.add('active');
  const loading = document.getElementById('page-loading');
  if (loading && n !== 'loading') loading.style.display = 'none';
  const burger = document.getElementById('burgerBtn');
  if (burger) burger.style.display = (n === 'auth') ? 'none' : '';
}

export function switchTab(t) {
  if (t !== 'qr') stopScan();
  document.querySelectorAll('.tab').forEach((b, i) => b.classList.toggle('active',
    (t === 'qr' && i === 0) || (t === 'sku' && i === 1) || (t === 'custom' && i === 2)));
  document.getElementById('tabqr').classList.toggle('active', t === 'qr');
  document.getElementById('tabsku').classList.toggle('active', t === 'sku');
  document.getElementById('tabcustom').classList.toggle('active', t === 'custom');
}

export function switchNav(page) {
  document.querySelectorAll('.bni').forEach(b => b.classList.remove('active'));
  document.getElementById('bni-' + page)?.classList.add('active');
  setDrawerActive(page);
  showPage(page);
  if (page === 'videos') loadVideoPage();
}

export function toggleAddCard() {
  document.getElementById('addMachineCard').classList.toggle('collapsed');
}

export function setAddCardCollapsed(collapsed) {
  const card = document.getElementById('addMachineCard');
  if (!card) return;
  card.classList.toggle('collapsed', collapsed);
}

/**
 * Åbner tilføj-panelet og ruller ned til det.
 *
 * Både feltet i maskingitteret og menupunktet i skuffen bruger denne.
 * Panelet er skjult når det er foldet sammen, så det skal foldes ud
 * FØR der rulles — ellers er der ikke noget at rulle hen til.
 */
export function aabnTilfoejMaskine() {
  setAddCardCollapsed(false);
  const card = document.getElementById('addMachineCard');
  if (!card) return;
  // Vent en optegning, så panelet har fået en position at rulle til.
  requestAnimationFrame(() => {
    card.scrollIntoView({ behavior: 'smooth', block: 'start' });
  });
}

// ── TEMA ──────────────────────────────────────
export function toggleTheme() {
  const isDark = document.body.classList.toggle('dark');
  document.getElementById('themeIcon').textContent = isDark ? '☀️' : '🌙';
  localStorage.setItem('theme', isDark ? 'dark' : 'light');
}

function anvendGemtTema() {
  if (localStorage.getItem('theme') !== 'dark') return;
  document.body.classList.add('dark');
  const icon = document.getElementById('themeIcon');
  if (icon) icon.textContent = '☀️';
}
